import * as React from 'react';
import { GoogleReviews } from 'lib/types';
import StarRatings from 'react-star-ratings';
import GoogleIcon from 'assets/google.inline.svg';
import X from 'assets/x.inline.svg';
import poweredByGoogle from 'assets/powered_by_google_on_white.png';
import * as styles from './GoogleReviewsBadge.module.scss';

type GoogleReviewsProps = {
    googleReviews: GoogleReviews;
    locationTitle?: string;
};

export function formatReviewToFloat(
    review?: string | number,
): number | undefined {
    return typeof review === 'string' ? parseFloat(review) : review;
}

const GoogleReviewsBadge: React.FunctionComponent<GoogleReviewsProps> = ({
    googleReviews,
    locationTitle = 'Bond Vet',
}) => {
    const [showReviews, setShowReviews] = React.useState(false);

    const averageRating = formatReviewToFloat(googleReviews.averageRating);

    return (
        <div>
            {showReviews && (
                <div className={styles.commentsRoot}>
                    <div className={styles.commentsTitleComponent}>
                        <div className={styles.commentsTitle}>
                            {locationTitle}
                        </div>
                        <div className={styles.badgeStars}>
                            <div className={styles.badgeStarsText}>
                                {averageRating?.toFixed(1)}
                            </div>
                            <div aria-label={`${averageRating} Stars`}>
                                <div aria-hidden="true">
                                    <StarRatings
                                        rating={averageRating}
                                        starDimension="20px"
                                        starSpacing="2px"
                                        starRatedColor="#e7711b"
                                    />
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            className={styles.closeButton}
                            onClick={() => setShowReviews(false)}
                        >
                            <X className={styles.xIcon} />
                        </button>
                    </div>
                    <div className={styles.commentsBody}>
                        {googleReviews.reviews.map((review, index) => {
                            return (
                                <div key={index} className={styles.commentRoot}>
                                    <img
                                        className={styles.profileImage}
                                        src={review.reviewer?.profilePhotoUrl}
                                        alt={review.reviewer?.displayName}
                                    />
                                    <div className={styles.commentContent}>
                                        <div>
                                            {review.reviewer?.displayName}
                                        </div>
                                        <div>
                                            <div
                                                aria-label={`${averageRating} Stars`}
                                            >
                                                <div aria-hidden="true">
                                                    <StarRatings
                                                        rating={review.rating}
                                                        starDimension="20px"
                                                        starSpacing="2px"
                                                        starRatedColor="#e7711b"
                                                    />
                                                </div>
                                            </div>
                                            <div className={styles.commentText}>
                                                {review.comment}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className={styles.footer}>
                        <img src={poweredByGoogle} alt="powered by Google" />
                    </div>
                </div>
            )}
            {!showReviews && (
                <div className={styles.badgeRoot}>
                    <div className={styles.badgeBorder} />
                    <button
                        type="button"
                        className={styles.badgeButton}
                        onClick={() => setShowReviews(true)}
                        disabled={!googleReviews.reviews}
                    >
                        <div className={styles.badgeWrapper}>
                            <div>
                                <GoogleIcon className={styles.googleIcon} />
                            </div>
                            <div className={styles.leftPadding}>
                                <div className={styles.badgeTitle}>
                                    Google Rating
                                </div>
                                <div className={styles.badgeStars}>
                                    <div className={styles.badgeStarsText}>
                                        {averageRating?.toFixed(1)}
                                    </div>
                                    <div aria-label={`${averageRating} Stars`}>
                                        <div aria-hidden="true">
                                            <StarRatings
                                                rating={averageRating}
                                                starDimension="20px"
                                                starSpacing="2px"
                                                starRatedColor="#e7711b"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.badgeText}>
                                    Based on {googleReviews.totalReviewCount}{' '}
                                    Reviews
                                </div>
                            </div>
                        </div>
                    </button>
                </div>
            )}
        </div>
    );
};

export default GoogleReviewsBadge;
